import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import './App.scss';

const LoginPage = lazy(() => import('./pages/login/index.jsx'));
const RegisterPage = lazy(() => import('./pages/register/index.jsx'));
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password/index.jsx'));
const ResetPasswordPage = lazy(() => import('./pages/reset-password/index.jsx'));
const ChangePasswordPage = lazy(() => import('./pages/change-password/index.jsx'));
const HomePage = lazy(() => import('./pages/home/index.jsx'));
const RoleListPage = lazy(() => import('./pages/roles/index.jsx'));
const UserListPage = lazy(() => import('./pages/users/index.jsx'));
const RolePermissionsPage = lazy(() => import('./pages/role-permissions/index.jsx'));
const CounterpartiesDirectoryPage = lazy(() => import('./pages/counterparties/index.jsx'));
const CounterpartyDetailPage = lazy(() => import('./pages/counterparties/detail.jsx'));
const DemandOrdersListPage = lazy(() => import('./pages/demand-orders/index.jsx'));
const DemandOrderDetailPage = lazy(() => import('./pages/demand-orders/detail.jsx'));
const WorkerProfilePage = lazy(() => import('./pages/workers/detail.jsx'));
const CandidatePoolSearchPage = lazy(() => import('./pages/workers/index.jsx'));
const CandidateMatchingGapAnalysisPage = lazy(() => import('./pages/requisition-matching/index.jsx'));
const AbsorptionCapacityMonitorPage = lazy(() => import('./pages/absorption-capacity/index.jsx'));
const GateWorklistPage = lazy(() => import('./pages/gates/index.jsx'));
const GateExecutionPage = lazy(() => import('./pages/gates/detail.jsx'));
const StatusRefreshQueuePage = lazy(() => import('./pages/status-refresh-queue/index.jsx'));
const WorkerCredentialRegistryPage = lazy(() => import('./pages/worker-credentials/index.jsx'));
const DocumentReviewQueuePage = lazy(() => import('./pages/document-review-queue/index.jsx'));
const ExpiryCollisionWatchlistPage = lazy(() => import('./pages/expiry-watchlist/index.jsx'));
const PackVersionManagementPage = lazy(() => import('./pages/pack-governance/index.jsx'));
const CostLedgerAttestationPage = lazy(() => import('./pages/cost-ledger/index.jsx'));
const ComplianceAuditLogExplorerPage = lazy(() => import('./pages/audit-log/index.jsx'));
const TenantSlaHorizonSettingsPage = lazy(() => import('./pages/tenant-settings/index.jsx'));
const RegulatorRegistryPage = lazy(() => import('./pages/regulator-registry/index.jsx'));
const CandidateTasksDocumentUploadPage = lazy(() => import('./pages/candidate-tasks/index.jsx'));
const CandidateDashboardPage = lazy(() => import('./pages/candidate-dashboard/index.jsx'));
const CandidateConsentIntroductionPage = lazy(() => import('./pages/candidate-consent/index.jsx'));
const CandidateCostStatementPage = lazy(() => import('./pages/candidate-cost-statement/index.jsx'));
const CounterpartyAccreditationPage = lazy(() => import('./pages/counterparty-accreditation/index.jsx'));
const CounterpartyRequisitionsPage = lazy(() => import('./pages/counterparty-requisitions/index.jsx'));
const CandidateReviewDecisionCenterPage = lazy(() => import('./pages/candidate-review/index.jsx'));
const PipelineCriticalPathDashboardPage = lazy(() => import('./pages/pipeline-critical-path-dashboard/index.jsx'));
const DeploymentTimelineBindingConstraintPage = lazy(() => import('./pages/deployment-timeline-binding-constraint/index.jsx'));
const PipelineSlippageAnalyticsPage = lazy(() => import('./pages/pipeline-slippage-analytics/index.jsx'));

const RoleListLayout = lazy(() => import('./layouts/_role-list-layout.jsx'));
const ChangePasswordLayout = lazy(() => import('./layouts/_change-password-layout.jsx'));
const CandidateTasksDocumentUploadLayout = lazy(() => import('./layouts/_candidate-tasks-document-upload-layout.jsx'));
const CounterpartyRequisitionsCandidatesLayout = lazy(() => import('./layouts/_counterparty-requisitions-candidates-layout.jsx'));
const CandidateDashboardLayout = lazy(() => import('./layouts/_candidate-dashboard-layout.jsx'));

// Generic status pages (create-frontend-layout-components Policy 10) — themed
// chrome that is not part of the raw design, created once. `404` is the router
// catch-all and the safe fallback target for any menuData nav link whose real
// page has not been built yet (see src/layouts/menuData.js).
const Unauthorized401 = lazy(() => import('./pages/status/Unauthorized401.jsx'));
const Forbidden403 = lazy(() => import('./pages/status/Forbidden403.jsx'));
const NotFound404 = lazy(() => import('./pages/status/NotFound404.jsx'));
const ServerError500 = lazy(() => import('./pages/status/ServerError500.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public — must be reachable before the auth guards below */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<RoleListLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route element={<RoleRoute allow={['Admin']} />}>
                  <Route path="/role-list" element={<RoleListPage />} />
                  <Route path="/user-list" element={<UserListPage />} />
                  <Route path="/role-permissions" element={<RolePermissionsPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Account Officer', 'Agency Manager', 'Admin']} />}>
                  <Route path="/counterparties-directory" element={<CounterpartiesDirectoryPage />} />
                  <Route path="/counterparty-detail-sites/:id" element={<CounterpartyDetailPage />} />
                  <Route path="/demand-orders-list" element={<DemandOrdersListPage />} />
                  <Route path="/demand-order-detail-requisitions/:id" element={<DemandOrderDetailPage />} />
                  <Route path="/absorption-capacity-monitor" element={<AbsorptionCapacityMonitorPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Sourcing Officer', 'Compliance Officer', 'Agency Manager', 'Admin']} />}>
                  <Route path="/worker-profile-service-history/:id" element={<WorkerProfilePage />} />
                  <Route path="/candidate-pool-search" element={<CandidatePoolSearchPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Sourcing Officer', 'Account Officer', 'Agency Manager', 'Admin']} />}>
                  <Route path="/candidate-matching-gap-analysis" element={<CandidateMatchingGapAnalysisPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Processing Staff', 'Compliance Officer', 'Admin']} />}>
                  <Route path="/gate-worklist-tasks" element={<GateWorklistPage />} />
                  <Route path="/gate-execution-evidence-capture/:id" element={<GateExecutionPage />} />
                  <Route path="/worker-credential-registry" element={<WorkerCredentialRegistryPage />} />
                  <Route path="/document-ingestion-review-queue" element={<DocumentReviewQueuePage />} />
                  <Route path="/status-refresh-queue" element={<StatusRefreshQueuePage />} />
                </Route>
                <Route element={<RoleRoute allow={['Compliance Officer', 'Agency Manager', 'Admin']} />}>
                  <Route path="/expiry-collision-watchlist" element={<ExpiryCollisionWatchlistPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Admin', 'Compliance Officer']} />}>
                  <Route path="/pack-version-management" element={<PackVersionManagementPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Agency Manager', 'Admin', 'Account Officer']} />}>
                  <Route path="/cost-ledger-attestation-generator" element={<CostLedgerAttestationPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Admin', 'Agency Manager', 'Compliance Officer']} />}>
                  <Route path="/compliance-audit-log-explorer" element={<ComplianceAuditLogExplorerPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Admin']} />}>
                  <Route path="/tenant-sla-horizon-settings" element={<TenantSlaHorizonSettingsPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Admin', 'Compliance Officer']} />}>
                  <Route path="/regulator-registry-fee-schedule" element={<RegulatorRegistryPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Agency Manager', 'Compliance Officer', 'Account Officer', 'Admin']} />}>
                  <Route path="/pipeline-critical-path-dashboard" element={<PipelineCriticalPathDashboardPage />} />
                  <Route path="/deployment-timeline-binding-constraint/:id" element={<DeploymentTimelineBindingConstraintPage />} />
                </Route>
                <Route element={<RoleRoute allow={['Agency Manager', 'Admin']} />}>
                  <Route path="/pipeline-health-slippage-analytics" element={<PipelineSlippageAnalyticsPage />} />
                </Route>
              </Route>
              <Route element={<ChangePasswordLayout />}>
                <Route path="/change-password" element={<ChangePasswordPage />} />
              </Route>
              <Route element={<CandidateTasksDocumentUploadLayout />}>
                <Route element={<RoleRoute allow={['Candidate']} />}>
                  <Route path="/candidate-tasks-document-upload" element={<CandidateTasksDocumentUploadPage />} />
                  <Route path="/candidate-consent-introduction" element={<CandidateConsentIntroductionPage />} />
                  <Route path="/candidate-deployment-cost-statement" element={<CandidateCostStatementPage />} />
                </Route>
              </Route>
              <Route element={<CandidateDashboardLayout />}>
                <Route element={<RoleRoute allow={['Candidate']} />}>
                  <Route path="/candidate-dashboard" element={<CandidateDashboardPage />} />
                </Route>
              </Route>
              <Route element={<CounterpartyRequisitionsCandidatesLayout />}>
                <Route element={<RoleRoute allow={['Counterparty Contact']} />}>
                  <Route path="/counterparty-accreditation-licensure" element={<CounterpartyAccreditationPage />} />
                  <Route path="/counterparty-requisitions-candidates" element={<CounterpartyRequisitionsPage />} />
                  <Route path="/candidate-review-decision-center/:id" element={<CandidateReviewDecisionCenterPage />} />
                </Route>
              </Route>
            </Route>

            {/* Per-page routes generated by ui-components-from-reference are nested
                under their layout (src/layouts/_*.jsx) and added here as pages are
                built — see src/layouts/menuData.js for the id/slug/name registry. */}

            <Route path="/401" element={<Unauthorized401 />} />
            <Route path="/403" element={<Forbidden403 />} />
            <Route path="/500" element={<ServerError500 />} />
            <Route path="*" element={<NotFound404 />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
